import {
  ElementDimensions,
  PositionCoords,
  PositionOption,
  PositionStrategy,
  PositionStrategyResult
} from "./position-strategy";

export class AnchorPositionStrategy extends PositionStrategy {
  constructor(
    private anchorElem: HTMLElement,
    private overlayPositions: PositionOption[],
    private anchorPadding: number = 0,
    private windowPadding: number = 20
  ) {
    super();
  }

  getPosition(
    clientRect: ClientRect,
    windowRef: Window
  ): PositionStrategyResult {
    let windowSize: ElementDimensions = this.getWindowDimensions(windowRef),
      anchorRect: ClientRect = this.anchorElem.getBoundingClientRect();

    let firstPositionCoords: PositionCoords | undefined;

    let i: number;
    const l: number = this.overlayPositions.length;
    for (i = 0; i < l; i++) {
      let positionCoords: PositionCoords = this.getPositionCoords(
        anchorRect,
        clientRect,
        windowSize,
        this.overlayPositions[i]
      );
      if (this.isInWindow(positionCoords, clientRect, windowSize)) {
        return {
          coords: positionCoords,
          position: this.overlayPositions[i]
        };
      }
      if (i === 0) {
        firstPositionCoords = positionCoords;
      }
    }

    return {
      coords: firstPositionCoords!,
      position: this.overlayPositions[0]
    };
  }

  private isInWindow(
    positionCoords: PositionCoords,
    clientRect: ClientRect,
    windowSize: ElementDimensions
  ): boolean {
    return Object.keys(positionCoords).every(
      (direction: keyof PositionCoords) => {
        let coord: number = positionCoords[direction]!;
        switch (direction) {
          case "top":
          case "bottom":
            return (
              coord >= this.windowPadding &&
              coord + clientRect.height <=
                windowSize.height - this.windowPadding
            );
          // default handles "left" and "right"
          default:
            return (
              coord >= this.windowPadding &&
              coord + clientRect.width <= windowSize.width - this.windowPadding
            );
        }
      }
    );
  }

  private getPositionCoords(
    anchorRect: ClientRect,
    clientRect: ClientRect,
    windowSize: ElementDimensions,
    overlayPosition: PositionOption
  ): PositionCoords {
    switch (overlayPosition) {
      case "top-left":
        return {
          bottom: windowSize.height - anchorRect.top + this.anchorPadding,
          left: anchorRect.left
        };
      case "bottom-left":
        return {
          top: anchorRect.bottom + this.anchorPadding,
          left: anchorRect.left
        };
      case "top-right":
        return {
          bottom: windowSize.height - anchorRect.top + this.anchorPadding,
          right: windowSize.width - anchorRect.right
        };
      case "bottom-right":
        return {
          top: anchorRect.bottom + this.anchorPadding,
          right: windowSize.width - anchorRect.right
        };
      case "top":
        return {
          bottom: windowSize.height - anchorRect.top + this.anchorPadding,
          left: anchorRect.left + anchorRect.width / 2 - clientRect.width / 2
        };
      case "bottom":
        return {
          top: anchorRect.bottom + this.anchorPadding,
          left: anchorRect.left + anchorRect.width / 2 - clientRect.width / 2
        };
      case "right":
        return {
          left: anchorRect.right + this.anchorPadding,
          top: anchorRect.top + anchorRect.height / 2 - clientRect.height / 2
        };
      case "left":
        return {
          right: windowSize.width - anchorRect.left + this.anchorPadding,
          top: anchorRect.top + anchorRect.height / 2 - clientRect.height / 2
        };
      case "center":
      case "dead-center":
        return {
          left: anchorRect.left + anchorRect.width / 2 - clientRect.width / 2,
          top: anchorRect.top + anchorRect.height / 2 - clientRect.height / 2
        };
      default:
        throw new Error("Invalid overlay position: " + overlayPosition);
    }
  }
}
